import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  XIcon,
  Drawer,
  Heading,
  IconButton,
  Input,
  RadioGroup,
  useBreakpoint,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
  Product,
} from '@buffer-mono/tracking-plan'

import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

import styles from './HeardAboutPrompt.module.css'

export const HEARD_ABOUT_PROMPT_SESSION_KEY = 'showHeardAboutPrompt'

const PROMPT_SOURCE = 'post-onboarding-card'

interface HeardAboutOption {
  id: string
  label: string
}

/**
 * @hardcoded The prompt question, option labels, and button copy are hardcoded,
 *   so product changes require a frontend deploy.
 * @fix Bring this into the onboarding flow API as a standard `SurveyStep`, or create
 *   a lightweight config endpoint. The former is preferred as it unifies the flow.
 */
const RANDOMIZABLE_OPTIONS: readonly HeardAboutOption[] = [
  { id: 'search-engine', label: 'Search engine' },
  {
    id: 'ai-search',
    label: 'AI search or chatbot (ChatGPT, Gemini, Claude, Perplexity, etc.)',
  },
  { id: 'friend', label: 'Friend, coworker or family member' },
  { id: 'social-network', label: 'On a social network' },
  { id: 'course', label: 'Took a course, class or training' },
  { id: 'returned-user', label: "I've used Buffer before" },
  { id: 'buffer-blog', label: 'The Buffer blog' },
  { id: 'buffer-youtube', label: 'The Buffer YouTube channel' },
  { id: 'buffer-team', label: 'Member of the Buffer team' },
]

const OTHER_OPTION: HeardAboutOption = {
  id: 'other',
  label: 'Other (please specify)',
}

function shuffleOptions(
  options: readonly HeardAboutOption[],
): HeardAboutOption[] {
  const shuffled = [...options]
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1))
    ;[shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ]
  }
  return shuffled
}

function OptionsList({
  className,
  options,
  value,
  onChange,
  otherText,
  onOtherTextChange,
}: {
  className?: string
  options: readonly HeardAboutOption[]
  value: string
  onChange: (value: string) => void
  otherText: string
  onOtherTextChange: (text: string) => void
}): JSX.Element {
  const isOtherSelected = value === 'other'
  const otherInputId = 'heard-about-other-input'

  return (
    <RadioGroup className={className} value={value} onChange={onChange}>
      {options.map((option) => (
        <div key={option.id} className={styles.optionItem}>
          <RadioGroup.Item
            value={option.id}
            aria-describedby={option.id === 'other' ? otherInputId : undefined}
          >
            {option.label}
          </RadioGroup.Item>
        </div>
      ))}
      <Input
        id={otherInputId}
        className={styles.otherInput}
        placeholder="Please specify"
        aria-label="Please specify how you heard about Buffer"
        disabled={!isOtherSelected}
        value={otherText}
        onChange={(e): void => onOtherTextChange(e.target.value)}
      />
    </RadioGroup>
  )
}

export function HeardAboutPrompt(): JSX.Element | null {
  const [visible, setVisible] = useState(
    () => sessionStorage.getItem(HEARD_ABOUT_PROMPT_SESSION_KEY) === 'true',
  )

  if (!visible) return null

  return <HeardAboutPromptContent onDismiss={(): void => setVisible(false)} />
}

function HeardAboutPromptContent({
  onDismiss,
}: {
  onDismiss: () => void
}): JSX.Element {
  const [options] = useState(() => [
    ...shuffleOptions(RANDOMIZABLE_OPTIONS),
    OTHER_OPTION,
  ])
  const breakpoint = useBreakpoint()
  const isMobile = breakpoint === 'initial' || breakpoint === 'xs'

  const commonTrackingProperties = useCommonTrackingProperties()
  const hasTrackedView = useRef(false)

  const flowSlug = sessionStorage.getItem('heardAboutPrompt_flowSlug') ?? ''
  const flowVersion = Number(
    sessionStorage.getItem('heardAboutPrompt_flowVersion') ?? '0',
  )

  const sharedTrackingProps = useMemo(
    () => ({
      ...commonTrackingProperties,
      clientName: Client.PublishWeb,
      product: Product.Publish,
      flowSlug,
      flowVersion,
      promptSource: PROMPT_SOURCE,
    }),
    [commonTrackingProperties, flowSlug, flowVersion],
  )

  useEffect(() => {
    if (hasTrackedView.current) return
    if (!commonTrackingProperties.organizationId) return

    hasTrackedView.current = true
    BufferTracker.onboardingHeardAboutPromptViewed(sharedTrackingProps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cleanupSessionStorage = useCallback((): void => {
    sessionStorage.removeItem(HEARD_ABOUT_PROMPT_SESSION_KEY)
    sessionStorage.removeItem('heardAboutPrompt_flowSlug')
    sessionStorage.removeItem('heardAboutPrompt_flowVersion')
  }, [])

  const dismiss = useCallback((): void => {
    cleanupSessionStorage()
    onDismiss()
  }, [cleanupSessionStorage, onDismiss])

  const [selection, setSelection] = useState('')
  const [otherText, setOtherText] = useState('')

  const isOtherSelected = selection === 'other'
  const canSubmit = selection && (!isOtherSelected || otherText.trim())

  const handleSubmit = useCallback((): void => {
    if (!canSubmit || !selection) return
    BufferTracker.onboardingHeardAboutPromptSubmitted({
      ...sharedTrackingProps,
      selectedOption: selection,
      selectedOptionOther: isOtherSelected ? otherText.trim() : null,
    })
    dismiss()
  }, [
    canSubmit,
    selection,
    isOtherSelected,
    otherText,
    sharedTrackingProps,
    dismiss,
  ])

  const handleDismiss = useCallback((): void => {
    BufferTracker.onboardingHeardAboutPromptDismissed(sharedTrackingProps)
    dismiss()
  }, [sharedTrackingProps, dismiss])

  if (isMobile) {
    return (
      <Drawer
        open
        onOpenChange={(open): void => {
          if (!open) handleDismiss()
        }}
      >
        <Drawer.Content>
          <Drawer.Header>
            <Drawer.Title>How did you hear about Buffer?</Drawer.Title>
          </Drawer.Header>
          <Drawer.Body>
            <OptionsList
              className={styles.drawerOptions}
              options={options}
              value={selection}
              onChange={setSelection}
              otherText={otherText}
              onOtherTextChange={setOtherText}
            />
            <Button
              className={styles.submitButton}
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer>
    )
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Heading size="sm">How did you hear about Buffer?</Heading>
        <IconButton
          variant="tertiary"
          size="small"
          label="Dismiss"
          onClick={handleDismiss}
        >
          <XIcon />
        </IconButton>
      </div>
      <OptionsList
        className={styles.options}
        options={options}
        value={selection}
        onChange={setSelection}
        otherText={otherText}
        onOtherTextChange={setOtherText}
      />
      <Button
        className={styles.submitButton}
        disabled={!canSubmit}
        onClick={handleSubmit}
      >
        Submit
      </Button>
    </div>
  )
}
